import type { SalesPoint } from 'pi-kiosk-shared';

type CustomerDirectorySalesPointEligibility = NonNullable<SalesPoint['directoryEligibility']>;
type CustomerDirectoryPublishGateCode = CustomerDirectorySalesPointEligibility['failedPublishGates'][number];

export type SalesPointDirectoryEligibilityViewModel = SalesPoint['directoryEligibility'];

/** Structural compare for G10 open-modal sync (avoid churn when SWR returns equal DTO). */
export function directoryEligibilityEquals(
  a: SalesPointDirectoryEligibilityViewModel,
  b: SalesPointDirectoryEligibilityViewModel,
): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (
    a.listable !== b.listable ||
    a.delistRisk !== b.delistRisk ||
    a.stickyOos !== b.stickyOos ||
    a.hasPublishablePay !== b.hasPublishablePay ||
    a.listedAt !== b.listedAt ||
    a.salesPointId !== b.salesPointId
  ) {
    return false;
  }
  if (a.failedPublishGates.length !== b.failedPublishGates.length) {
    return false;
  }
  return a.failedPublishGates.every((gate, index) => gate === b.failedPublishGates[index]);
}

export function isProductSalesPoint(salesPoint: SalesPoint): boolean {
  return salesPoint.salesPointOperationalMode !== 'DONATION';
}

export function shouldShowSalesPointDirectoryCallout(
  eligibility: SalesPointDirectoryEligibilityViewModel,
): boolean {
  if (eligibility == null) {
    return false;
  }
  return (
    eligibility.delistRisk ||
    eligibility.failedPublishGates.length > 0 ||
    (!eligibility.listable && !eligibility.stickyOos)
  );
}

export interface AggregatedSalesPointsDirectoryEligibility {
  readonly listable: boolean;
  readonly listedAt: string | null;
  readonly hasPublishablePay: boolean;
  readonly failedPublishGates: readonly CustomerDirectoryPublishGateCode[];
  readonly stickyOos: boolean;
  readonly delistRisk: boolean;
  readonly notListableCount: number;
  readonly delistRiskCount: number;
  readonly stickyOosCount: number;
}

export function aggregateSalesPointsDirectoryEligibility(
  salesPoints: readonly SalesPoint[],
): AggregatedSalesPointsDirectoryEligibility | null {
  const productSalesPoints = salesPoints.filter(isProductSalesPoint);
  if (productSalesPoints.length === 0) {
    return null;
  }

  const rows = productSalesPoints
    .map((sp) => sp.directoryEligibility)
    .filter((row): row is CustomerDirectorySalesPointEligibility => row != null);
  if (rows.length === 0) {
    return null;
  }
  const gateSet = new Set<CustomerDirectoryPublishGateCode>();
  let listedAt: string | null = null;
  let listable = false;
  let hasPublishablePay = false;
  let stickyOos = false;
  let delistRisk = false;
  let notListableCount = 0;
  let delistRiskCount = 0;
  let stickyOosCount = 0;

  for (const row of rows) {
    if (row.listable) {
      listable = true;
    } else if (!row.stickyOos) {
      notListableCount += 1;
    }
    if (row.hasPublishablePay) {
      hasPublishablePay = true;
    }
    if (row.stickyOos) {
      stickyOos = true;
      stickyOosCount += 1;
    }
    if (row.delistRisk) {
      delistRisk = true;
      delistRiskCount += 1;
    }
    if (row.listedAt != null) {
      if (listedAt === null || row.listedAt < listedAt) {
        listedAt = row.listedAt;
      }
    }
    for (const gate of row.failedPublishGates) {
      gateSet.add(gate);
    }
  }

  return {
    listable,
    listedAt,
    hasPublishablePay,
    failedPublishGates: [...gateSet],
    stickyOos,
    delistRisk,
    notListableCount,
    delistRiskCount,
    stickyOosCount,
  };
}

export function directoryEligibilityStatusLabelKey(
  eligibility: SalesPointDirectoryEligibilityViewModel,
): string {
  if (eligibility == null) {
    return 'kiosks.directoryEligibility.statusNotListed';
  }
  if (eligibility.delistRisk) {
    return 'kiosks.directoryEligibility.statusDelistRisk';
  }
  if (eligibility.listable && eligibility.stickyOos) {
    return 'kiosks.directoryEligibility.statusStickyOos';
  }
  if (eligibility.listable) {
    return 'kiosks.directoryEligibility.statusListed';
  }
  return 'kiosks.directoryEligibility.statusNotListed';
}

export function directoryEligibilityStatusTone(
  eligibility: SalesPointDirectoryEligibilityViewModel,
): 'success' | 'warn' | 'danger' | 'neutral' {
  if (eligibility == null) {
    return 'neutral';
  }
  if (eligibility.delistRisk) {
    return 'danger';
  }
  if (eligibility.listable && eligibility.stickyOos) {
    return 'warn';
  }
  if (eligibility.listable) {
    return 'success';
  }
  return 'neutral';
}

/** Shared SalesPoint may omit commerce JSON; list/detail still carry it when present. */
type SalesPointWithOptionalCommerce = SalesPoint & {
  readonly commerceConfigJson?: unknown;
};

/**
 * True when SP commerce JSON has Mobilní obchod explicitly off (operator kill switch under SPI ALLOW).
 * Used for admin strip honesty when PAY_READY fails because shop rails are gated off.
 */
export function isMobileShopOffFromCommerce(commerceConfigJson: unknown): boolean {
  if (commerceConfigJson == null || typeof commerceConfigJson !== 'object') {
    return false;
  }
  return (commerceConfigJson as { mobileShopEnabled?: unknown }).mobileShopEnabled === false;
}

/**
 * Aggregated list/account honesty: PAY_READY is shop-off when any product SP fails PAY_READY
 * with Mobilní obchod explicitly off in commerce JSON.
 */
export function hasPayReadyMobileShopOffAmongSalesPoints(
  salesPoints: readonly SalesPoint[],
): boolean {
  for (const salesPoint of salesPoints) {
    if (!isProductSalesPoint(salesPoint)) {
      continue;
    }
    const eligibility = salesPoint.directoryEligibility;
    if (eligibility == null || !eligibility.failedPublishGates.includes('PAY_READY')) {
      continue;
    }
    const commerce = (salesPoint as SalesPointWithOptionalCommerce).commerceConfigJson;
    if (isMobileShopOffFromCommerce(commerce)) {
      return true;
    }
  }
  return false;
}

/**
 * Fail-closed: true when stored commerce enables kiosk cash rail (Approach A prerequisite).
 * Mirrors backend `salesPointHasKioskCashRailEnabled` for list-banner jump honesty.
 */
export function isKioskCashRailEnabledFromCommerce(commerceConfigJson: unknown): boolean {
  if (commerceConfigJson == null || typeof commerceConfigJson !== 'object') {
    return false;
  }
  const rails = (commerceConfigJson as { paymentRailsKiosk?: unknown }).paymentRailsKiosk;
  if (rails == null || typeof rails !== 'object' || Array.isArray(rails)) {
    return false;
  }
  return (rails as { cash?: unknown }).cash === true;
}

/**
 * List-banner / Account-parity Approach A signal from SP list rows already on screen.
 * True when any product SP fails PAY_READY without publishable pay and has kiosk cash rail on
 * (cash-box remediation on Prodejní kanály — not banks Ověřit).
 */
export function hasCashApproachARemediationAmongSalesPoints(
  salesPoints: readonly SalesPoint[],
): boolean {
  for (const salesPoint of salesPoints) {
    if (!isProductSalesPoint(salesPoint)) {
      continue;
    }
    const eligibility = salesPoint.directoryEligibility;
    if (
      eligibility == null ||
      eligibility.hasPublishablePay ||
      !eligibility.failedPublishGates.includes('PAY_READY')
    ) {
      continue;
    }
    const commerce = (salesPoint as SalesPointWithOptionalCommerce).commerceConfigJson;
    if (isKioskCashRailEnabledFromCommerce(commerce)) {
      return true;
    }
  }
  return false;
}

export function resolveDirectoryPublishGateLabelKey(
  gate: CustomerDirectoryPublishGateCode,
  options?: { readonly mobileShopOff?: boolean },
): string {
  if (gate === 'PAY_READY' && options?.mobileShopOff === true) {
    return 'account.directoryVisibility.gates.MOBILE_SHOP_OFF';
  }
  return `account.directoryVisibility.gates.${gate}`;
}

/**
 * Jump target for directory publish-gate remediation.
 * In-modal tabs: overview / commerce / payments.
 * Leave-modal: inventory, products, bank-accounts, account-org, account-go-live.
 */
export type DirectoryGateModalTab =
  | 'overview'
  | 'commerce'
  | 'payments'
  | 'inventory'
  | 'products'
  | 'bank-accounts'
  | 'account-org'
  | 'account-go-live';

/** Targets that close the SP modal and navigate elsewhere. */
export const DIRECTORY_GATE_EXTERNAL_TARGETS = [
  'inventory',
  'products',
  'bank-accounts',
  'account-org',
  'account-go-live',
] as const satisfies readonly DirectoryGateModalTab[];

export type DirectoryGateExternalTarget = (typeof DIRECTORY_GATE_EXTERNAL_TARGETS)[number];

export function isDirectoryGateExternalTarget(
  target: DirectoryGateModalTab,
): target is DirectoryGateExternalTarget {
  return (DIRECTORY_GATE_EXTERNAL_TARGETS as readonly string[]).includes(target);
}

function salesPointFailsGate(
  salesPoint: SalesPoint,
  gate: CustomerDirectoryPublishGateCode,
): boolean {
  const eligibility = salesPoint.directoryEligibility;
  return eligibility != null && eligibility.failedPublishGates.includes(gate);
}

/**
 * Pick the product SP that owns the remediated condition for an in-modal banner jump.
 * External targets do not need an owner (return null). Prefer condition owners over
 * first-callout-only ordering.
 */
export function selectSalesPointForDirectoryBannerJump(
  salesPoints: readonly SalesPoint[],
  target: DirectoryGateModalTab,
  options?: {
    readonly mobileShopOff?: boolean;
    readonly cashApproachA?: boolean;
  },
): SalesPoint | null {
  if (isDirectoryGateExternalTarget(target)) {
    return null;
  }

  const productSalesPoints = salesPoints.filter(isProductSalesPoint);

  const firstMatching = (
    predicate: (salesPoint: SalesPoint) => boolean,
  ): SalesPoint | null => {
    for (const salesPoint of productSalesPoints) {
      if (predicate(salesPoint)) {
        return salesPoint;
      }
    }
    return null;
  };

  if (target === 'commerce') {
    if (options?.mobileShopOff === true) {
      const shopOffOwner = firstMatching((salesPoint) => {
        if (!salesPointFailsGate(salesPoint, 'PAY_READY')) {
          return false;
        }
        const commerce = (salesPoint as SalesPointWithOptionalCommerce).commerceConfigJson;
        return isMobileShopOffFromCommerce(commerce);
      });
      if (shopOffOwner != null) {
        return shopOffOwner;
      }
    }
    if (options?.cashApproachA === true) {
      const cashOwner = firstMatching((salesPoint) => {
        const eligibility = salesPoint.directoryEligibility;
        if (
          eligibility == null ||
          eligibility.hasPublishablePay ||
          !eligibility.failedPublishGates.includes('PAY_READY')
        ) {
          return false;
        }
        const commerce = (salesPoint as SalesPointWithOptionalCommerce).commerceConfigJson;
        return isKioskCashRailEnabledFromCommerce(commerce);
      });
      if (cashOwner != null) {
        return cashOwner;
      }
    }
    const payReadyOwner = firstMatching((salesPoint) =>
      salesPointFailsGate(salesPoint, 'PAY_READY'),
    );
    if (payReadyOwner != null) {
      return payReadyOwner;
    }
  }

  if (target === 'payments') {
    const paymentsOwner = firstMatching(
      (salesPoint) =>
        salesPointFailsGate(salesPoint, 'PAY_READY') ||
        salesPointFailsGate(salesPoint, 'BANK_METHOD') ||
        salesPointFailsGate(salesPoint, 'METHOD_READINESS'),
    );
    if (paymentsOwner != null) {
      return paymentsOwner;
    }
  }

  if (target === 'overview') {
    const overviewOwner = firstMatching(
      (salesPoint) =>
        salesPointFailsGate(salesPoint, 'SALES_POINT') ||
        salesPointFailsGate(salesPoint, 'STRUCTURAL') ||
        salesPointFailsGate(salesPoint, 'STOCK'),
    );
    if (overviewOwner != null) {
      return overviewOwner;
    }
  }

  return (
    firstMatching((salesPoint) =>
      shouldShowSalesPointDirectoryCallout(salesPoint.directoryEligibility),
    ) ?? null
  );
}

export type DirectoryGateModalTabOptions = {
  readonly mobileShopOff?: boolean;
  /** True when Mobilní obchod editor is available (SPI write entitled). */
  readonly mobileShopEditorAvailable?: boolean;
  /** True when Prodejní kanály tab is in the modal tab list. */
  readonly commerceTabAvailable?: boolean;
  /**
   * True when the user can reach Inventory chrome (same SoT as Dashboard SAG:
   * snapshotReady ∧ inventory_management read ∧ effective ops:inventory:read).
   * Callers precompute; fail-closed when unset/false — STOCK → overview (never Prodejní kanály).
   */
  readonly inventorySectionAccessible?: boolean;
  /**
   * Legacy option: Prodejní kanály capability toggles (OPI / inventory_management).
   * Unused for STOCK qty jumps — that tab does not edit quantityInStock.
   * Kept for call-site compatibility; STOCK never resolves to commerce.
   */
  readonly stockActionableOnCommerce?: boolean;
  /**
   * Cash Approach A: PAY_READY / bank gates remediate on this SP cash box
   * (Prodejní kanály), not Account → Bankovní účty Ověřit.
   */
  readonly cashApproachA?: boolean;
};

/**
 * Gate jump honesty: send operators only where the fix actually lives.
 * STOCK qty → Inventory (or overview fallback). Shop-off Mobilní obchod → Prodejní kanály
 * when editable. Bank verify / products / org / go-live leave the modal to those screens.
 */
export function resolveDirectoryGateModalTab(
  gate: CustomerDirectoryPublishGateCode,
  options?: DirectoryGateModalTabOptions,
): DirectoryGateModalTab {
  // STOCK qty remediation = Inventory dashboard when accessible — never Prodejní kanály.
  // stockActionableOnCommerce is intentionally ignored (no qty editor there).
  if (gate === 'STOCK') {
    return options?.inventorySectionAccessible === true ? 'inventory' : 'overview';
  }
  if (gate === 'PRODUCT') {
    return 'products';
  }
  if (gate === 'ORG_APPROVAL') {
    return 'account-org';
  }
  if (gate === 'TENANT_LIVE') {
    return 'account-go-live';
  }
  // PAY_READY + shop off → Mobilní obchod on Prodejní kanály only when SPI write + tab can flip it.
  if (gate === 'PAY_READY' && options?.mobileShopOff === true) {
    if (
      options.mobileShopEditorAvailable === true &&
      options.commerceTabAvailable === true
    ) {
      return 'commerce';
    }
    // Cannot toggle shop here — still send to bank verify when payments are also missing.
    return 'bank-accounts';
  }
  // Cash Approach A → cash box on Prodejní kanály (not banks Ověřit).
  if (
    options?.cashApproachA === true &&
    (gate === 'PAY_READY' || gate === 'BANK_METHOD' || gate === 'METHOD_READINESS')
  ) {
    return 'commerce';
  }
  // Electronic verify / bank method — Bankovní účty (Ověřit), not SP payments tab.
  if (gate === 'PAY_READY' || gate === 'BANK_METHOD' || gate === 'METHOD_READINESS') {
    return 'bank-accounts';
  }
  // Name/address/active — stay on this sales point overview.
  return 'overview';
}
